import { useEffect, useState, useRef, useCallback } from "react";
import {
  FaPaperPlane,
  FaSpinner,
  FaFileAlt,
  FaFileImage,
  FaFileArchive,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileCsv,
} from "react-icons/fa";
import "../css/chatBox.css";
import { useParams, useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import { webSocketService } from "../services/websocketService";
import { apiService } from "../services/api";
import AttachmentButton from "./attachments/attachmentButton";
import AttachmentPreview from "./attachments/attachmentPreview";
import { MessageType, WebChatMessage } from "../utils/WebChatMessage";
import CountryCodeSelector, { countries } from "./webchat/countryCode";
import AgentUnavailableUI from "./webchat/agentOfflineScreen";
import Cookies from "js-cookie";

const ChatBox = () => {
  const { officeName } = useParams();
  const [searchParams] = useSearchParams();
  let source = searchParams.get("source");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [officeDid, setOfficeDid] = useState("");
  const [showChat, setShowChat] = useState(false);
  const chatBoxRef = useRef(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [phone, setPhone] = useState("");
  const [startCount, setStartCount] = useState(0);
  const [endCount, setEndCount] = useState(20);
  const [error, setError] = useState(null);
  const socketConnectedRef = useRef(false);
  const lastMessageIdRef = useRef(null);
  const [agentAvailable, setAgentAvailable] = useState(false);
  const availabilityIntervalRef = useRef(null);
  const sessionCheckIntervalRef = useRef(null);
  const tabVisibleRef = useRef(true);
  const [attachments, setAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [countryCode, setCountryCode] = useState(countries[0]);
  const [sessionEnd, setSessionEnd] = useState(null);

  const formattedPhone = countryCode.code.substring(1) + phone;

  if (!searchParams.has("source")) {
    source = "Tax";
  }

  // console.info(`office: ${officeName} Source: ${source}`);

  if (source !== "Health_Insurance" && source !== "Tax") {
    throw new Error("Invalid source!");
  }

  // Check session status
  const checkSessionStatus = async () => {
    const sessionId = Cookies.get("WebChatSessionID");

    if (!sessionId) {
      console.log("No session ID found");
      return;
    }

    try {
      const response = await fetch(
        apiService.websiteChatBox.checkSessionStatus(sessionId),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Session status:", data);

      if (!data.active) {
        setSessionEnd(true);
        clearInterval(sessionCheckIntervalRef.current);

        // Clear the session cookie
        Cookies.remove("WebChatSessionID");
      }
    } catch (error) {
      console.error("Error checking session status:", error);
    }
  };

  // Set up session check interval when session starts
  useEffect(() => {
    const sessionId = Cookies.get("WebChatSessionID");

    console.log("Session ID found:", sessionId);
    console.log("showChat status:", showChat);
    console.log("Current interval ref:", sessionCheckIntervalRef.current);

    if (showChat && sessionId) {
      // Initial check
      checkSessionStatus();

      // Clear any existing interval before setting a new one
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }

      // Set up interval
      sessionCheckIntervalRef.current = setInterval(checkSessionStatus, 10000);
      console.log("Started session status check interval");
    }

    return () => {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
        sessionCheckIntervalRef.current = null;
        console.log("Cleared session status check interval");
      }
    };
  }, [showChat, sessionEnd]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = ""; // Triggers the confirmation dialog
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const wasReloaded = sessionStorage.getItem("wasReloaded");

    if (wasReloaded) {
      // Page was reloaded
      Cookies.remove("WebChatSessionID");
      sessionStorage.removeItem("wasReloaded");
    }

    window.addEventListener("beforeunload", () => {
      sessionStorage.setItem("wasReloaded", "true");
    });

    return () => {
      window.removeEventListener("beforeunload", () => {
        sessionStorage.setItem("wasReloaded", "true");
      });
    };
  }, []);

  // Check agent availability
  const checkAgentAvailability = async () => {
    // Don't check availability if chat is already shown

    console.log("Entering agent availability...");
    console.log(showChat);
    console.log(!sessionEnd);
    console.log(agentAvailable);

    if (sessionEnd == true) {
      return;
    }

    console.log("Checking agent availability...");

    try {
      const response = await fetch(
        apiService.websiteChatBox.checkAgentAvailability(officeName, phone),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Agent availability: ", data.available && !data.busy);
      setAgentAvailable(data.available && !data.busy);
    } catch (error) {
      console.error("Error checking agent availability:", error);
      setAgentAvailable(false);
    }
  };

  // Set up visibility change handler
  // useEffect(() => {
  //   // Function to handle visibility changes
  //   const handleVisibilityChange = () => {
  //     if (document.visibilityState === "visible") {
  //       console.log("Tab is now visible");
  //       tabVisibleRef.current = true;

  //       // Only check availability if chat is not shown yet
  //       if (!agentAvailable && officeName) {
  //         console.log("Tab is visible - checking agent availability");
  //         checkAgentAvailability();

  //         // Restart the interval if it's not running and chat is not shown
  //         if (!availabilityIntervalRef.current) {
  //           availabilityIntervalRef.current = setInterval(
  //             checkAgentAvailability,
  //             5000
  //           );
  //           console.log(
  //             "Started availability check interval after tab visibility change"
  //           );
  //         }
  //       }
  //     } else {
  //       console.log("Tab is now hidden");
  //       tabVisibleRef.current = false;

  //       // Clear the interval when tab is not visible to save resources
  //       if (availabilityIntervalRef.current) {
  //         clearInterval(availabilityIntervalRef.current);
  //         availabilityIntervalRef.current = null;
  //         console.log("Cleared availability check interval due to tab hidden");
  //       }
  //     }
  //   };

  //   // Add event listener for visibility change
  //   document.addEventListener("visibilitychange", handleVisibilityChange);

  //   return () => {
  //     // Remove the event listener on cleanup
  //     document.removeEventListener("visibilitychange", handleVisibilityChange);
  //   };
  // }, [officeName, showChat]); // Added showChat as dependency

  // Set up periodic availability check (every 5 seconds)
  useEffect(() => {
    // Only set up interval if we have an office name and chat is not shown
    if (officeName && !agentAvailable) {
      // Initial check
      checkAgentAvailability();
      getOfficeDid();

      // Set up interval for periodic checks only if showChat is false
      if (!availabilityIntervalRef.current) {
        availabilityIntervalRef.current = setInterval(
          checkAgentAvailability,
          5000
        );
        console.log("Started agent availability check interval");
      }
    } else if (showChat && availabilityIntervalRef.current) {
      // Clear interval when showChat becomes true
      clearInterval(availabilityIntervalRef.current);
      availabilityIntervalRef.current = null;
      console.log(
        "Stopped checking agent availability because chat is now shown"
      );
    }

    // Clean up interval on component unmount or when showChat changes
    return () => {
      if (availabilityIntervalRef.current) {
        clearInterval(availabilityIntervalRef.current);
        availabilityIntervalRef.current = null;
        console.log("Cleaned up availability check interval");
      }
    };
  }, [officeName, showChat]);

  // Check availability on focus/click - but only if chat is not shown
  useEffect(() => {
    // Additional event listener for user interaction
    const handleUserInteraction = () => {
      if (tabVisibleRef.current && officeName && !agentAvailable) {
        checkAgentAvailability();
      }
    };

    window.addEventListener("focus", handleUserInteraction);
    document.addEventListener("click", handleUserInteraction);

    return () => {
      window.removeEventListener("focus", handleUserInteraction);
      document.removeEventListener("click", handleUserInteraction);
    };
  }, [officeName, showChat]); // Added showChat as dependency

  // Update messages
  const updateMessages = useCallback((newData) => {
    setMessages((prevMessages) => {
      const combinedMessages = [...prevMessages, ...newData];

      const uniqueMessages = combinedMessages
        .filter((message, index, self) => {
          return message.messageId !== null;
        })
        .sort((a, b) => a.messageId - b.messageId);

      // Update last message ID reference
      if (uniqueMessages.length > 0) {
        const highestIdMessage = [...uniqueMessages].sort(
          (a, b) => b.messageId - a.messageId
        )[0];
        lastMessageIdRef.current = highestIdMessage.messageId;
      }

      console.log("After updated: ", uniqueMessages);

      return uniqueMessages;
    });
  }, []);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback(
    (message) => {
      console.log("handling web socket messages");
      console.log("Previous messsages: ", messages);
      console.log(message);

      try {
        // Check if message is already a parsed object
        let messageData =
          typeof message === "object" ? message : JSON.parse(message);

        if (messageData && messageData.sender) {
          if (messageData.type === "SYSTEM") {
            let sessionInfo = messageData.content.split("-");
            let sessionMessage = sessionInfo[0];
            let sessionId = sessionInfo[1];
            console.log("Web Session ID : ", sessionId);

            if (sessionMessage === "Session started") {
              Cookies.set("WebChatSessionID", sessionId);
              setSessionEnd(false);
            } else {
              Cookies.remove("WebChatSessionID");
            }
            console.log(sessionMessage);
          } else {
            updateMessages([messageData]);

            // If user is at the bottom, scroll to bottom when new message arrives
            if (chatBoxRef.current) {
              const { scrollTop, scrollHeight, clientHeight } =
                chatBoxRef.current;
              if (scrollTop + clientHeight >= scrollHeight - 30) {
                setTimeout(scrollToBottom, 0);
              }
            }
          }
        }
      } catch (err) {
        console.error("Error processing WebSocket message:", err);
      }
    },
    [updateMessages]
  );

  // Initialize WebSocket connection
  const initializeWebSocket = () => {
    if (!socketConnectedRef.current && formattedPhone) {
      try {
        webSocketService.connect(formattedPhone, handleWebSocketMessage);
        socketConnectedRef.current = true;
        console.log(`WebSocket connected for ${formattedPhone}`);
      } catch (err) {
        console.error("WebSocket connection error:", err);
        toast.dismiss();
        toast.error(`WebSocket connection failed: ${err.message}`);
      }
    }
  };

  useEffect(() => {
    if (showChat && socketConnectedRef.current === false) {
      setInterval(initializeWebSocket, 3000);
    }

    return () => {
      if (socketConnectedRef.current) {
        clearInterval(socketConnectedRef.current);
      }
    };
  }, [socketConnectedRef.current]);

  // File upload handler
  const handleAttachmentSelect = async (files) => {
    if (files.length === 0) return;

    // Limit number of attachments
    if (files.length + attachments.length > 5) {
      toast.error("Maximum 5 attachments allowed");
      return;
    }

    // Validate file sizes
    const invalidFiles = files.filter((file) => file.size > 5 * 1024 * 1024); // 5MB limit
    if (invalidFiles.length > 0) {
      toast.error("Files must be less than 5MB");
      return;
    }

    setIsUploading(true);
    try {
      // Upload each file
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("phone", formattedPhone);

        const response = await fetch(apiService.websiteChatBox.uploadFile(), {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          throw new Error("File upload failed");
        }

        const data = await response.json();
        return {
          fileName: data.fileName, // Unique filename from server
          originalName: file.name,
          fileUrl: data.fileUrl,
          fileType: file.type,
        };
      });

      const uploadedFileDetails = await Promise.all(uploadPromises);

      // Update state
      setUploadedFiles((prev) => [...prev, ...uploadedFileDetails]);
      setAttachments((prev) => [...prev, ...files]);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload files");
    } finally {
      setIsUploading(false);
    }
  };

  // Remove attachment handler
  const handleRemoveAttachment = (index) => {
    // Remove from attachments
    setAttachments((prev) => prev.filter((_, i) => i !== index));

    // Remove from uploaded files
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Save login data
  const saveLogin = async () => {
    const agentIdFromCookie = getCookie("AgentID");

    if (!agentIdFromCookie) {
      return;
    }

    try {
      const response = await fetch(
        apiService.websiteChatBox.getConversation(
          formattedPhone,
          agentIdFromCookie
        ),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phone: formattedPhone,
            login: agentIdFromCookie,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save login data");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Function to get a cookie by name
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };

  // Get office DID
  function getOfficeDid() {
    fetch(apiService.websiteChatBox.getOfficeDetails(officeName), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.text();
      })
      .then((data) => {
        setOfficeDid(data);
        console.log("Success:", data);
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }

  // Fetch messages
  const fetchMessages = (isScrollToTop = false) => {
    if (!hasMoreMessages) return;

    const chatBox = chatBoxRef.current;
    const previousScrollHeight = chatBox ? chatBox.scrollHeight : 0;

    fetch(
      apiService.websiteChatBox.getMessages(
        formattedPhone,
        startCount,
        endCount
      )
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch messages");
        }
        return response.json();
      })
      .then((data) => {
        // Ensure 'data' is an array before processing
        if (!Array.isArray(data)) {
          console.error("Expected an array of messages, but got:", data);
          return;
        }

        if (data.length === 0) {
          setHasMoreMessages(false);
        }

        setMessages((prevMessages) => {
          const allMessages = isScrollToTop
            ? [...data, ...prevMessages]
            : [...prevMessages, ...data];

          const uniqueMessages = allMessages.filter(
            (message, index, self) =>
              index === self.findIndex((m) => m.id === message.id)
          );

          return uniqueMessages;
        });
      })
      .catch((err) => {
        setError(err.message);
      })
      .finally(() => {
        setStartCount(startCount + 20);
        setEndCount(endCount + 20);
        console.log("Start Count: " + startCount);
        console.log("End Count: " + endCount);
        // Restore the scroll position
        setTimeout(() => {
          if (chatBox) {
            chatBox.scrollTop =
              chatBox.scrollTop + (chatBox.scrollHeight - previousScrollHeight);
          }
        }, 0);
      });
  };

  // Handle scroll for loading more messages
  const handleScroll = useCallback(
    debounce(() => {
      console.log("Scroll top: " + chatBoxRef.current?.scrollTop);
      if (chatBoxRef.current?.scrollTop === 0 && hasMoreMessages) {
        fetchMessages(true);
      }
    }, 300),
    [hasMoreMessages]
  );

  // Attach and detach scroll event
  useEffect(() => {
    const chatBox = chatBoxRef.current;
    if (chatBox) {
      chatBox.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (chatBox) {
        chatBox.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  // Scroll to bottom conditionally
  const scrollToBottomConditionally = () => {
    if (chatBoxRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatBoxRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;
      if (isAtBottom) {
        scrollToBottom();
      }
    }
  };

  // Auto-scroll effects
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottomConditionally();
    }
  }, [messages]);

  // Scroll to bottom
  const scrollToBottom = () => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  };

  // Render attachment in message
  const renderMessageAttachments = (message) => {
    if (!message.images || message.images.length === 0) return null;

    return (
      <div className="message-attachments mt-1">
        {message.images.map((attachment, index) => {
          const isImage = attachment.fileType.startsWith("image/");
          console.log("Is image: ", isImage);

          return isImage ? (
            <div key={index} className="attachment-preview mb-1">
              <img
                src={attachment.fileUrl}
                alt={attachment.originalName}
                className="max-w-[150px] max-h-[150px] rounded border border-gray-300"
              />
            </div>
          ) : (
            <div
              key={index}
              className="attachment-link text-blue-500 underline text-xs"
            >
              <a
                href={attachment.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {attachment.originalName}
              </a>
            </div>
          );
        })}
      </div>
    );
  };

  // Clean up WebSocket connection and intervals on component unmount
  useEffect(() => {
    return () => {
      // Clean up WebSocket
      if (socketConnectedRef.current) {
        try {
          webSocketService.disconnect(formattedPhone);
          socketConnectedRef.current = false;
          console.log(`WebSocket disconnected for ${formattedPhone}`);
        } catch (err) {
          console.error("WebSocket disconnect error:", err);
        }
      }

      // Clean up availability check interval
      if (availabilityIntervalRef.current) {
        clearInterval(availabilityIntervalRef.current);
        availabilityIntervalRef.current = null;
      }
    };
  }, [formattedPhone]);

  // File type icon mapping
  const getFileTypeIcon = (fileType) => {
    const iconMap = {
      "image/": FaFileImage,
      "application/pdf": FaFilePdf,
      "application/msword": FaFileWord,
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        FaFileWord,
      "application/vnd.ms-excel": FaFileExcel,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        FaFileExcel,
      "text/csv": FaFileCsv,
      "application/zip": FaFileArchive,
      "application/x-zip-compressed": FaFileArchive,
    };

    const matchedIcon = Object.entries(iconMap).find(([key]) =>
      fileType.startsWith(key)
    );

    return matchedIcon ? matchedIcon[1] : FaFileAlt;
  };

  // Enhanced send message to include name
  const handleSendMessage = async () => {
    if (input.trim() === "" && uploadedFiles.length === 0) return;

    try {
      if (uploadedFiles.length > 0) {
        console.log("uploaded files: ", uploadedFiles);
        console.log("Attachments: ", attachments);

        uploadedFiles.map(async (attachment, index) => {
          let webChatMessage = new WebChatMessage();
          webChatMessage.callType = "Website";
          webChatMessage.content = attachment.fileName;
          webChatMessage.fileUrl = attachment.fileUrl;
          webChatMessage.fileInfo = {
            fileName: attachment.fileName,
            fileUrl: attachment.fileUrl,
            fileSize: attachments[index].size,
            fileType: attachment.fileType,
          };
          webChatMessage.type = MessageType.FILE;
          webChatMessage.senderId = null;
          webChatMessage.sender = "Customer";
          webChatMessage.senderName = customerName || "Customer";
          webChatMessage.phone = formattedPhone;
          webChatMessage.to = officeDid;
          webChatMessage.from = formattedPhone;
          webChatMessage.messageId = null;
          webChatMessage.source = source ?? "Tax";

          setMessages((prev) => [...prev, webChatMessage]);

          if (agentAvailable) {
            await webSocketService.sendMessage(webChatMessage);
          } else {
            const response = await fetch(
              apiService.websiteChatBox.saveCustomerInfo(),
              {
                method: "POST",
                body: webChatMessage,
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );

            if (!response.ok) {
              console.error("Error saving customer data");
            }

            let agentReply = new WebChatMessage();
            agentReply.content = `Hi ${
              customerName || "there"
            }! Our support team is currently offline. We'll respond to your inquiry as soon as possible.`;
            agentReply.senderId = null;
            agentReply.sender = "Agent";
            agentReply.senderName = "Support Team";
            agentReply.phone = formattedPhone;
            agentReply.to = officeDid;
            agentReply.from = formattedPhone;
            agentReply.messageId = null;
            agentReply.source = source ?? "Tax";

            setTimeout(() => {
              setMessages((prev) => [...prev, agentReply]);
            }, 1500);
          }
        });
      } else {
        let messageToBeSent = WebChatMessage.createTextMessage(
          null,
          formattedPhone,
          officeDid,
          null,
          customerName || "Customer",
          input.trim(),
          source
        );

        setMessages((prev) => [...prev, messageToBeSent]);

        if (!agentAvailable) {
          console.log(messageToBeSent);

          const response = await fetch(
            apiService.websiteChatBox.saveCustomerInfo(),
            {
              method: "POST",
              body: JSON.stringify(messageToBeSent),
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          if (!response.ok) {
            console.error("Error saving customer data");
          }

          let agentReply = new WebChatMessage();
          agentReply.content = `Hi ${
            customerName || "there"
          }! Our support team is currently unavailable. We'll respond to your inquiry as soon as possible.`;
          agentReply.senderId = null;
          agentReply.sender = "Agent";
          agentReply.senderName = "Support Team";
          agentReply.phone = formattedPhone;
          agentReply.to = officeDid;
          agentReply.from = formattedPhone;
          agentReply.messageId = null;
          agentReply.source = source;

          setTimeout(() => {
            setMessages((prev) => [...prev, agentReply]);
          }, 1500);
        } else {
          await webSocketService.sendMessage(messageToBeSent);
        }
      }
      // Clear input and attachments
      setInput("");
      setAttachments([]);
      setUploadedFiles([]);
    } catch (error) {
      console.error("Send message error:", error);
      toast.error("Failed to send message");
    }
  };

  // useEffect(() => {
  //   return async () => {
  //     let messageToBeSent = WebChatMessage.createTextMessage(
  //       null,
  //       formattedPhone,
  //       officeDid,
  //       null,
  //       customerName || "Customer",
  //       "Customer end the session!",
  //       source
  //     );
  //     Cookies.remove("WebChatSessionID");
  //     await webSocketService.sendMessage(messageToBeSent);
  //   };
  // }, []);

  // Render message content with file/image previews
  const renderMessageContent = (message) => {
    if (message.type === "FILE") {
      const FileIcon = getFileTypeIcon(message.fileInfo?.fileType || "");

      // Check if it's an image
      const imageExtensions = [
        ".png",
        ".jpg",
        ".jpeg",
        ".gif",
        ".bmp",
        ".webp",
        ".svg",
      ];
      const isImage = message.fileInfo?.fileName
        ? imageExtensions.some((ext) =>
            message.fileInfo.fileName.toLowerCase().endsWith(ext)
          )
        : false;

      const styles = {
        container: {
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
        },
        image: {
          maxWidth: "150px",
          maxHeight: "150px",
          borderRadius: "4px",
          border: "1px solid #ccc",
        },
        fileIconContainer: {
          display: "flex",
          alignItems: "center",
        },
        fileIcon: {
          marginRight: "8px",
        },
      };

      console.log("is image: ", isImage);
      console.log(message.fileUrl);

      return (
        <div
          onClick={() =>
            window.open(message.fileUrl.replace("view", "download"))
          }
          style={styles.container}
        >
          {isImage ? (
            <img
              src={message.fileUrl}
              alt={message.content}
              style={styles.image}
            />
          ) : (
            <div style={styles.fileIconContainer}>
              <FileIcon style={styles.fileIcon} />
              <span>{message.content}</span>
            </div>
          )}
        </div>
      );
    }
    return <>{message.content}</>;
  };

  // Update the form submission to capture customer name
  const handleSubmit = (e) => {
    e.preventDefault();
    const nameInput = e.target.querySelector('input[placeholder="Name"]');
    setCustomerName(nameInput.value);
    saveLogin();
    getOfficeDid();
    setShowChat(true);
    initializeWebSocket();

    // Stop any ongoing agent availability checks
    if (availabilityIntervalRef.current) {
      clearInterval(availabilityIntervalRef.current);
      availabilityIntervalRef.current = null;
      console.log("Stopped checking agent availability due to session start");
    }
  };

  return (
    <div
      style={{ width: "380px", height: "450px", display: "flex" }}
      className="bg-opacity-10 bg-white border border-gray-300 rounded-2xl shadow-2xl backdrop-blur-lg max-w-sm mx-auto"
    >
      {!showChat ? (
        <form
          className="p-6"
          style={{ width: "350px", height: "400px", justifyContent: "center" }}
          onSubmit={handleSubmit}
        >
          <div className="mb-4">
            <input
              className="border border-gray-300 p-3 rounded-lg shadow-sm w-full focus:border-blue-500 focus:ring-4 focus:ring-blue-400 outline-none"
              type="text"
              placeholder="Name"
              required
            />
          </div>
          <div className="mb-4">
            <input
              className="border border-gray-300 p-3 rounded-lg shadow-sm w-full focus:border-blue-500 focus:ring-4 focus:ring-blue-400 outline-none"
              type="email"
              placeholder="Email"
            />
          </div>
          <div style={{ marginBottom: "auto" }}>
            <CountryCodeSelector onSelect={setCountryCode} />
            <input
              className="border border-gray-300 p-3 rounded-lg shadow-sm w-full focus:border-blue-500 focus:ring-4 focus:ring-blue-400 outline-none"
              type="tel"
              placeholder="Phone"
              required
              pattern="\d{10}"
              maxLength="10"
              inputMode="numeric"
              value={phone}
              onChange={(e) => {
                let newValue = e.target.value.replace(/\D/g, ""); // Remove non-numeric characters
                setPhone(newValue);
              }}
            />
          </div>
          <button
            type="submit"
            className="bg-blue-500 p-3 rounded-lg text-white w-full font-bold hover:bg-blue-600"
          >
            Start Chat
          </button>
        </form>
      ) : agentAvailable ? (
        <div
          style={{ width: "350px", height: "400px" }}
          className="flex flex-col bg-white p-6 rounded-b-2xl w-full"
        >
          <div
            ref={chatBoxRef}
            className="flex-grow border border-gray-300 rounded-lg chat-messages-container mb-4 overflow-y-auto"
          >
            {messages.length === 0 && (
              <p
                style={{
                  color: "#6B7280",
                  fontSize: "0.95rem",
                  lineHeight: "1.5",
                  margin: "auto",
                  maxWidth: "180px",
                  opacity: 0.7,
                }}
              >
                <strong>
                  Ask your{" "}
                  {source === "Health_Insurance" ? "Insurance" : "Tax Filing"}{" "}
                  related queries here!!!
                </strong>
              </p>
            )}
            {messages.map((message, index) => (
              <div
                key={message.id || index}
                style={{
                  maxWidth: "80%",
                  marginLeft: message.sender !== "Customer" ? 0 : "auto",
                  marginBottom: "4px",
                  textAlign: "left",
                  alignSelf:
                    message.sender !== "Customer" ? "flex-start" : "flex-end",
                }}
                className={`chat-messag ${
                  message.sender !== "Customer"
                    ? "agent-messag"
                    : "customer-messag text-right"
                }`}
              >
                <div
                  style={{
                    backgroundColor:
                      message.sender !== "Customer"
                        ? "#E6F2FF" // Light blue background for agent messages
                        : "#F3F4F6", // Light gray for customer messages
                    color:
                      message.sender !== "Customer"
                        ? "#000" // Black text for agent messages
                        : "#1F2937", // Dark gray for customer messages
                    padding: "0.5rem",
                    borderRadius: "0.5rem",
                    display: "inline-block",
                    maxWidth: "20rem",
                    wordBreak: "break-word",
                  }}
                >
                  {renderMessageContent(message)}
                </div>
              </div>
            ))}
          </div>

          {/* Attachment preview area */}
          <AttachmentPreview
            files={attachments}
            onRemove={handleRemoveAttachment}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Attachment button */}
            <AttachmentButton
              onAttachmentSelect={handleAttachmentSelect}
              disabled={isUploading}
            />
            <input
              className="flex-grow border border-gray-300 p-2 rounded-lg message-input"
              type="text"
              placeholder="Type a message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={async (e) =>
                e.key === "Enter" && (await handleSendMessage())
              }
            />
            <button
              onClick={async () => await handleSendMessage()}
              disabled={isUploading}
              className={`send-button ml-2 p-2 ${
                isUploading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              } rounded-full`}
            >
              {isUploading ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <FaPaperPlane />
              )}
            </button>
          </div>
          {sessionEnd && (
            <div
              style={{
                marginTop: "10px",
                backgroundColor: "orange",
                color: "white",
                padding: "3px",
              }}
            >
              Session has ended!
            </div>
          )}
        </div>
      ) : (
        <AgentUnavailableUI name={customerName} source={source} />
      )}
    </div>
  );
};

export default ChatBox;
